/**
 * La REP emballages, et la question qui décide de tout : qui est « producteur ».
 *
 * La responsabilité élargie du producteur fait peser sur celui qui MET SUR LE
 * MARCHÉ national un produit emballé le coût de la fin de vie de cet emballage
 * (articles L. 541-10 et suivants du code de l'environnement). Le mot est
 * trompeur : on n'a rien à fabriquer pour être producteur, il suffit de vendre
 * sous sa marque, d'importer, ou d'expédier un colis.
 *
 * TROIS SITUATIONS QUE TOUT LE MONDE MÉLANGE :
 *   - vendre sous SA marque un produit emballé, ou l'importer : producteur pour
 *     l'emballage du produit ;
 *   - revendre un produit déjà mis sur le marché français par un autre : ce
 *     n'est PAS vous le producteur de son emballage, c'est celui qui l'a mis sur
 *     le marché le premier ;
 *   - expédier un colis, quoi qu'il y ait dedans : le carton, le calage et
 *     l'adhésif sont des emballages de service, et là vous êtes producteur,
 *     même en revendant la marque d'un autre.
 * La deuxième et la troisième se cumulent souvent chez un micro-entrepreneur
 * qui revend en ligne, et c'est pour cela qu'il se croit hors du champ.
 *
 * LE PPWR NE REMPLACE RIEN AUJOURD'HUI. Le règlement (UE) 2025/40 s'applique
 * depuis le 12 août 2026, et pose dès lors le principe d'emballages
 * recyclables, la limite des métaux lourds, celle des PFAS dans les emballages
 * alimentaires et la déclaration UE de conformité (douane, 25/09/2026) ; les
 * critères de recyclabilité et le réemploi n'arrivent qu'en 2030. Les
 * obligations françaises restent donc entières, et c'est elles que ce module
 * diagnostique.
 */

/** Ce que coûte l'absence d'identifiant unique, et celle de la signalétique. */
export const AMENDES = {
    identifiant: 30000,
    signaletique: { personnePhysique: 3000, societe: 15000 },
};

/** Les éco-organismes agréés pour la filière des emballages ménagers. */
export const ECO_ORGANISMES = ["Citeo", "Adelphe", "Leko"];

/** Les dates du règlement européen, pour ne pas les confondre. */
export const PPWR = { applicableDepuis: "12 août 2026", grosDesObligations: 2030 };

/**
 * Ce qui fait de vous un producteur, selon ce que vous vendez et comment.
 *
 * Rend la liste des emballages dont vous répondez : ceux du produit, ceux de
 * l'expédition, ou aucun. Deux réponses peuvent être vraies en même temps.
 */
export function responsabilites(reponses) {
    const r = reponses || {};
    const liste = [];
    // Mettre sur le marché sous sa marque ou importer : l'emballage du produit
    // est le vôtre, même si un autre l'a fabriqué.
    if (r.produits === "maMarque" || r.produits === "import") liste.push("emballageProduit");
    // Le colis est un emballage de service, et il ne dépend pas de la marque de
    // ce qu'il contient.
    if (r.expedie === "oui") liste.push("emballageExpedition");
    return liste;
}

/**
 * Qui doit s'enregistrer, et qui n'a rien à faire.
 *
 * Deux portes de sortie : ne vendre aucun produit physique à des particuliers,
 * et ne répondre d'aucun emballage. Une troisième situation ne ferme pas la
 * porte mais déplace l'obligation : la place de marché qui déclare pour vous.
 */
export function concerne(reponses) {
    const r = reponses || {};
    if (!r.clients) return { concerne: null, motif: null };
    if (r.clients === "professionnels") return { concerne: false, motif: "horsMenages" };
    if (!r.produits) return { concerne: null, motif: null };
    if (r.produits === "aucun") return { concerne: false, motif: "aucunProduit" };
    if (!r.expedie) return { concerne: null, motif: null };

    if (responsabilites(r).length === 0) return { concerne: false, motif: "revendeurSansColis" };
    if (r.placeDeMarche === "oui") return { concerne: false, motif: "placeDeMarche" };
    return { concerne: true, motif: null };
}

/** Ce qui manque pour être en règle, dans l'ordre où il faut s'en occuper. */
export function manquements(reponses) {
    const r = reponses || {};
    if (!concerne(r).concerne) return [];

    const liste = [];
    // L'adhésion vient en premier : c'est l'éco-organisme qui fait délivrer
    // l'identifiant, jamais l'inverse.
    if (r.ecoOrganisme !== "oui") liste.push("adherer");
    if (r.identifiant !== "oui") liste.push("identifiant");
    if (r.afficheIdentifiant !== "oui") liste.push("afficherIdentifiant");
    if (responsabilites(r).includes("emballageProduit") && r.signaletique !== "oui") {
        liste.push("signaletique");
    }
    return liste;
}

/** L'amende de signalétique encourue, selon la forme de l'entreprise. */
export function amendeSignaletique(reponses) {
    return (reponses || {}).forme === "societe"
        ? AMENDES.signaletique.societe
        : AMENDES.signaletique.personnePhysique;
}

/** Le diagnostic complet, tel que le parcours l'affiche. */
export function diagnostic(reponses) {
    const r = reponses || {};
    const tri = concerne(r);
    if (tri.concerne !== true) {
        return { concerne: false, motif: tri.motif, manques: [], enRegle: false, responsabilites: [] };
    }

    const manques = manquements(r);
    return {
        concerne: true,
        motif: null,
        responsabilites: responsabilites(r),
        manques,
        enRegle: manques.length === 0,
        amendeIdentifiant: AMENDES.identifiant,
        amendeSignaletique: amendeSignaletique(r),
    };
}

/** Ce que le parcours récapitule avant les actions. */
export function recapitulatif(reponses) {
    const etat = diagnostic(reponses);
    if (!etat.concerne) return null;
    const r = reponses;
    const oui = (v) => (v === "oui" ? "Oui" : "Non");

    const quoi = etat.responsabilites
        .map((c) => (c === "emballageProduit" ? "l'emballage de vos produits" : "vos colis d'expédition"))
        .join(" et ");

    return [
        ["Vous répondez de", quoi],
        ["Adhésion à un éco-organisme", oui(r.ecoOrganisme)],
        ["Identifiant unique obtenu", oui(r.identifiant)],
        ["Identifiant affiché sur le site et les conditions", oui(r.afficheIdentifiant)],
        ...(etat.responsabilites.includes("emballageProduit")
            ? [["Triman et consignes de tri sur l'emballage", oui(r.signaletique)]]
            : []),
    ];
}
